import React, { useEffect } from 'react';
import { X } from 'lucide-react';

/**
 * AtelierModal — the one modal chrome.
 *
 * Replaces the four scrim variants that shipped across the app
 * (bg-black/40, bg-black bg-opacity-50, bg-slate-600 bg-opacity-50,
 * bg-black/50) with a single warm-ink scrim and the Atelier card
 * treatment: rounded-card, shadow-atelier-lg, mono eyebrow + Cormorant
 * title in header, ink dividers, rounded-btn footer buttons.
 *
 * This shell owns ONLY the chrome. Body content is passed verbatim;
 * callers preserve their existing form/markup/handlers unchanged. The
 * shell does not extract or reorganize anything — it only swaps the
 * outer wrapper.
 */
export default function AtelierModal({
  open = true,
  onClose,
  eyebrow,
  title,
  footer,
  children,
  maxWidth = 'max-w-lg',
  closeOnScrim = true,
  showClose = true,
  bodyClassName = 'p-5',
  zIndex = 50,
}) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && onClose) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ background: 'rgba(42,37,32,0.42)', zIndex }}
      onClick={closeOnScrim && onClose ? () => onClose() : undefined}
      role="presentation"
    >
      <div
        className={`bg-paper-elev rounded-card shadow-atelier-lg w-full ${maxWidth} mx-4 max-h-[90vh] overflow-y-auto`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        {(title || eyebrow || showClose) && (
          <div className="px-5 py-4 border-b border-line flex items-start justify-between gap-3">
            <div className="min-w-0">
              {eyebrow && <div className="av-eyebrow mb-1">{eyebrow}</div>}
              {title && (
                <h2 className="font-display text-ink truncate" style={{ fontSize: '1.25rem', fontWeight: 500, lineHeight: 1.2 }}>
                  {title}
                </h2>
              )}
            </div>
            {showClose && onClose && (
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="flex-shrink-0 text-ink-3 hover:text-ink rounded-btn p-1 -mr-1"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        )}
        <div className={bodyClassName}>{children}</div>
        {footer && (
          <div className="px-5 py-3 border-t border-line flex items-center justify-end gap-2">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
